import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import {
    Dashboard,
    SettingsSuggest,
    AutoGraph,
    AssignmentTurnedIn,
    ArrowBack,
} from "mosaic-react-icons";
import { parse } from "query-string";

import I18n from "../../I18n";
import theme from "../../style/_theme.json";

import { mapProperties, hydrateRoute, number, parseFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
} from "../../store/actions";

import { List, Filters, LoadingIndicator, ImageRender } from "colbi_web_ui/lib/components";
import Header from "colbi_web_ui/lib/components/layout/Header/Header";

import { EntityCard } from "colbi_web_ui/lib/components/rows";
import { FloatButton } from "colbi_web_ui/lib/components/inputs";

import { manageNavigation } from "./navigation";

import styles from "./Settings.module.sass";

import { UserRoles } from "../../enums/userRoles";
import configsByTheme from "../../enums/configsByTheme";
import { getOrganizationName } from "../../utils/camelize";
import { submitTicketFormData } from "../../store/actions/modalActions";
import { REACT_APP_COLBI_THEME } from "../../environmentVariables";

import {
    decodeUserSession,
    buildStaticLinks,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";
import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import GlobalLayout from "../../components/GlobalLayout";

const allTabs = [
    {
        section: "organizations",
        label: "Organizations",
        createType: "Organization",
    },
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const filtersTabs = [
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "";
const asideMenuLogo = configsByTheme(appTheme)["icon"];
const localesByAppTheme = configsByTheme(appTheme)["locales"];
const freshDeskFormTitleByAppTheme = configsByTheme(appTheme)["freshDeskFormTitle"];

const logo = () => {
    return (
        appTheme && <ImageRender icon={`${appTheme && appTheme.length ? asideMenuLogo : "logo"}`} />
    );
};

const Settings = (props) => {
    const {
        user,
        fetch,
        match,
        setLocale,
        location,
        toggleMenu,
        logout,
        history,
        pushModal,
        menu,
        reload,
    } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);

    const headerTitle =
        match.params.section === "kpis"
            ? `settings_menu_${match?.params?.section || ""}`
            : `${match?.params?.section || ""}`;

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const appVersion = fetch?.results[`${locale}_application_version`]?.version;

    const userInformation = decodeUserSession(user);

    const isSettingsRoles = [
        UserRoles.ADMIN,
        UserRoles.BACKOFFICE,
        UserRoles.SUPPORT,
        UserRoles.PP_SUPPORT,
    ];

    const tabs = !checkAccessByRole(userInformation, isSettingsRoles) ? filtersTabs : allTabs;

    const isAdminRoles = !checkAccessByRole(userInformation, [
        UserRoles.OWNER,
        UserRoles.READER,
        UserRoles.WRITER,
    ]);

    const manageNavigationItems = [
        {
            name: "Home",
            to: "/",
            exact: true,
            icon: ArrowBack,
        },
    ];

    if (isAdminRoles) {
        manageNavigationItems.unshift(
            {
                name: "Rules",
                to: "/settings/nc_rules",
                exact: true,
                icon: AssignmentTurnedIn,
            },
            {
                name: "Kpis",
                to: "/settings/kpis",
                exact: true,
                icon: AutoGraph,
            },
            {
                name: "Custom Reports",
                to: "/settings/custom_reports",
                exact: true,
                icon: SettingsSuggest,
            },
            {
                name: "Packages",
                to: "/settings/packages",
                exact: true,
                icon: Dashboard,
            },
        );

        tabs.push(
            { section: "packages", label: "Packages", createType: "Package" },
            {
                section: "custom_reports",
                label: "Custom Reports",
                createType: "CustomReportSettings",
            },
            { section: "kpis", label: "Kpis", createType: "Kpi" },
            { section: "nc_rules", label: "Rules", createType: "NcRule" },
        );
    }

    const navigation = manageNavigation(manageNavigationItems);

    if (!checkAccessByRole(userInformation, isSettingsRoles)) {
        navigation.shift();
    }

    if (checkAccessByRole(userInformation, [UserRoles.BACKOFFICE])) {
        navigation.pop();
    }

    const staticLinks = buildStaticLinks(navigation, history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true);

    if (redirect) {
        return redirect;
    }

    if (!(userInformation && userInformation.accessSettings)) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    (navigation || []).forEach((elem) => {
        elem.name = I18n.translate`${elem.name}`;
        return elem;
    });

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const data = mapProperties(
        {
            items: "query(0).list",
            header: "query(0).header",
            footer: "query(0).footer",
            metadata: "query(0).metadata",
            page: "param(page)",
            status: "status(0)",
            pageSize: "param(pageSize)",
            pageDefault: 10,
            filter: "param(filter)",
            sort: "param(sort)",
            availableFilters: "query(0).filters",
        },
        (fetch || {}).results,
        (fetch || {}).status,
        (Settings.queries && Settings.queries(params)) || [],
        props,
        params,
        i18n,
        locale,
    );

    const currentSection = match.params.section || "/";
    let floatCreateTypename = (tabs.find(({ section }) => section === currentSection) || {})
        .createType;

    const ticketSubjectEnvironment = fetch.results[`${locale}_get_environment_to_subject`] || "";

    const isToDisplaySupportIcon =
        (fetch.results[`${locale}_is_to_display_support_icon`] || "false").toLocaleLowerCase() ===
        "true";

    if (currentSection === "/") {
        const basePath = "/settings/:organizationId?";
        const hydrated = hydrateRoute(basePath, { ...params });
        return <Redirect to={`${hydrated}${tabs[0].section}`} replace />;
    }

    const metadata = getOrganizationName();

    const navProps = BuildSovosNavProps(
        undefined,
        [],
        { name: "" },
        {},
        userInformation,
        staticLinks || [],
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries: "",
        params,
        staticLinks,
        styles,
        version: appVersion,
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <div className={styles["main"]}>
                <Header
                    className={styles["header"]}
                    logout={logout}
                    navigation={navigation}
                    locales={localesByAppTheme}
                    i18n={i18n}
                    locale={locale}
                    search={false}
                    history={history}
                    params={{
                        ...match.params,
                    }}
                    theme={theme}
                    title={i18n`${headerTitle}`}
                    toggleMenu={toggleMenu}
                    expanded={menu !== "expanded"}
                    menuLogo={logo}
                    menuTitle={i18n`Settings`}
                    displaySupportIcon={isToDisplaySupportIcon}
                    environment={ticketSubjectEnvironment}
                    emailFrom={userInformation.email}
                    name={userInformation.name}
                    metadata={metadata}
                    freshDeskModalTitle={freshDeskFormTitleByAppTheme}
                />
                <main className={`${styles["content"] || ""} ${styles["foreground"] || ""}`}>
                    {fetch.activity && (
                        <LoadingIndicator
                            className={`${styles["loading-indicator"]} ${
                                menu === "expanded" ? "" : styles["small-margin"]
                            }`}
                        />
                    )}
                    <Filters
                        filter={data.filter}
                        availableFilters={data.availableFilters}
                        stickyTop={theme}
                        anchor="results"
                    />
                    <List
                        modifiers={["--root-margin-top-small", "--row-style-alternate"]}
                        {...(data || {})}
                        card={EntityCard}
                        onClick={(e) => {
                            e.originalEvent.preventDefault();
                            e.originalEvent.stopPropagation();
                            const { id, card, __typename } = e.target.value || {};
                            const title = card && card[0] && card[0][0] ? card[0][0].value : "";
                            pushModal({
                                title,
                                view: `${__typename}Form`,
                                args: { id },
                            })
                                .then(() => {
                                    history.push(match.url);
                                    reload && reload();
                                })
                                .catch(() => null);
                        }}
                    />
                    {floatCreateTypename ? (
                        <FloatButton
                            className={styles["float-button"]}
                            icon={`new-${(floatCreateTypename || "").toLowerCase()}`}
                            onClick={() => {
                                pushModal({
                                    title: i18n`New ${floatCreateTypename}`,
                                    view: `${floatCreateTypename}Form`,
                                    data: { __typename: floatCreateTypename },
                                })
                                    .then(() => {
                                        reload && reload();
                                    })
                                    .catch(() => null);
                            }}
                        />
                    ) : null}
                </main>
            </div>
        </GlobalLayout>
    );
};

Settings.queries = (args) => {
    const { section, pageSize, page, filter, locale } = args || {};
    const pagesWithLocale = ["nc_rules", "packages", "kpis", "custom_reports"];

    return section
        ? [
              {
                  resource: section,
                  args: {
                      pageSize: number(pageSize),
                      page: page ? number(page) : 0,
                      locale: pagesWithLocale.includes(section) ? locale : undefined,
                      filter: filter
                          ? { type: "[FilterInput!]", value: parseFilter(filter) }
                          : undefined,
                      sort: args.sort
                          ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                          : undefined,
                  },
                  body: `__typename, items{...${section}},hasMore,total,filters{ prop, name, type, list, disableOperators }`,
              },
              {
                  resource: "application_version",
                  body: "version",
              },
              {
                  resource: "is_to_display_support_icon",
              },
              {
                  resource: "get_environment_to_subject",
              },
          ]
        : [];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        uploadFiles: uploadAction,
        submitTicket: submitTicketFormData,
        submitAuditNote: auditNoteSubmit,
    },
)(withRouter(Settings));
